/**
 * Standalone entry (catalogue page, QA page, CDN bundle).
 *
 * Embedding from Shopify (Phase 4) uses the same entry: the theme block sets
 * `window.MATA_TRYON_CONFIG` (frame spec from metafields, lang, assetBase) and
 * calls `window.MataTryOn.open()`.
 *
 * A42F: the standalone page is the Handmade catalogue (`#mt-catalogue`). The
 * try-on opens over it; « Tout voir » closes the try-on and comes back to it.
 */
import './styles.css';
import './catalogue/catalogue.css';
import { warmEngine, warmSelectedFrame } from './app/preload';
import { TryOnApp, type TryOnConfig } from './app/TryOnApp';
import type { FrameSpec } from './config/frames';
import { FRAME_INDEX as FRAME_CATALOGUE, frameForHandle, loadCatalogueFrame } from './config/catalogue';
import { PhonePanel } from './measurement/PhonePanel';
import { CataloguePage, catalogueSearch } from './catalogue/CataloguePage';
import { FramesView } from './ui/FramesView';
import { detectLang, getLang } from './ui/i18n';
import { translator } from './ui/localized';
import { isLine, isShape, lineFromSlug, type CatalogueLine } from './config/collections';

type HostConfig = Partial<TryOnConfig> & {
  frame?: Partial<FrameSpec>;
  /** A42F: page opened by « Tout voir » (defaults to the catalogue page of this release). */
  catalogueUrl?: string;
  /**
   * A200: Shopify product handle of the page (the section no longer keeps a list of frames). The
   * frame is found by its product handle; an unknown handle opens nothing (Fittingbox stays).
   */
  productHandle?: string;
};
interface HostWindow extends Window {
  MATA_TRYON_CONFIG?: HostConfig;
  MataTryOn?: { open: (config?: Partial<TryOnConfig>) => TryOnApp; catalogue: FrameSpec[]; loadFrame: typeof loadCatalogueFrame };
}

const host = window as HostWindow;
const params = new URLSearchParams(location.search);
const HANDMADE = FRAME_CATALOGUE.filter(f => f.line === 'Handmade');
/** A200: the Signature collection (data line `Classic`): its own page, its own list. */
const SIGNATURE = FRAME_CATALOGUE.filter(f => f.line === 'Classic');
const LISTS: Record<CatalogueLine, readonly FrameSpec[]> = { Handmade: HANDMADE, Classic: SIGNATURE };
const phonePanel = params.get('measure') === '1' ? new PhonePanel() : null;
const catalogueRoot = document.getElementById('mt-catalogue');
/** A200: the collection of this standalone page (`signature/index.html` sets data-line="Classic"). */
const pageLine: CatalogueLine = isLine(catalogueRoot?.dataset.line) ? catalogueRoot!.dataset.line as CatalogueLine : 'Handmade';
const legacyLauncher = document.getElementById('mt-launch');
let catalogue: CataloguePage | null = null;

function resolveConfig(overrides: Partial<TryOnConfig> = {}): TryOnConfig {
  const fromHost = host.MATA_TRYON_CONFIG ?? {};
  const byHandle = overrides.frame?.id ? undefined : frameForHandle(fromHost.productHandle);
  if (fromHost.productHandle && !overrides.frame?.id && !fromHost.frame?.id && !byHandle) throw new Error(`Product missing from release: ${fromHost.productHandle}`);
  const requestedLine = overrides.catalogueLine ?? fromHost.catalogueLine ?? lineFromSlug(params.get('collection'));
  const frameId = overrides.frame?.id ?? fromHost.frame?.id ?? byHandle?.id ?? params.get('frame');
  // A200: a frame always opens in its own collection (a Signature handle never lands in the Handmade list).
  const known = frameId ? FRAME_CATALOGUE.find(f => f.id === frameId) : undefined;
  const catalogueLine: CatalogueLine | undefined = known && requestedLine ? known.line : requestedLine;
  const choices = catalogueLine ? FRAME_CATALOGUE.filter(f => f.line === catalogueLine) : FRAME_CATALOGUE;
  if (frameId && !known && !overrides.frame?.silhouette && !overrides.frame?.glbUrl && !fromHost.frame?.glbUrl) throw new Error(`Unknown frame: ${frameId}`);
  if (catalogueLine && frameId && !choices.some(f => f.id === frameId)) throw new Error('Frame outside the collection');
  const catalogueFrame = choices.find((f) => f.id === frameId) ?? choices[0]!;
  const frame: FrameSpec = { ...catalogueFrame, ...(fromHost.frame as Partial<FrameSpec>), ...(overrides.frame ?? {}) };
  const glb = params.get('glb');
  if (glb) frame.glbUrl = glb;
  return {
    ...fromHost,
    ...overrides,
    frame,
    catalogueLine,
    orderProductHandle: overrides.orderProductHandle ?? fromHost.orderProductHandle ?? fromHost.productHandle,
    loadFrameSpec: frame.silhouette || frame.glbUrl ? undefined : () => loadCatalogueFrame(frame.id).then(full => ({ ...full, ...(fromHost.frame as Partial<FrameSpec>), ...(overrides.frame ?? {}) })),
    debug: overrides.debug ?? fromHost.debug ?? params.get('debug') === '1',
    trackerMode: (params.get('tracker') as TryOnConfig['trackerMode']) ?? overrides.trackerMode ?? fromHost.trackerMode ?? 'auto',
    mirror: params.get('mirror') === '0' ? false : (overrides.mirror ?? fromHost.mirror ?? true),
    skipConsent: params.get('consent') === '0' ? true : (overrides.skipConsent ?? fromHost.skipConsent ?? false),
  };
}

/**
 * « Tout voir » from a Shopify product page: the catalogue page of this release,
 * in the same tab, with the frame on screen pre-selected. A host can point it
 * elsewhere with `MATA_TRYON_CONFIG.catalogueUrl`.
 */
function catalogueHref(config: TryOnConfig, frame: FrameSpec, line: CatalogueLine | undefined = frame.line): string {
  const fromHost = host.MATA_TRYON_CONFIG?.catalogueUrl;
  const root = config.assetBase ? new URL(config.assetBase, location.href) : new URL('../', import.meta.url);
  // A200: the Signature catalogue is its own page (`signature/`) of the release.
  const base = line === 'Classic' ? new URL('signature/', root.href.endsWith('/') ? root.href : `${root.href}/`) : fromHost && config.catalogueLine === line ? new URL(fromHost, location.href) : root;
  const url = new URL(base.href);
  if (frame.line === (line ?? frame.line)) url.searchParams.set('frame', frame.id);
  url.searchParams.set('lang', getLang());
  return url.href;
}

/** A200: the standalone page of the other collection (Handmade at the release root, Signature in `signature/`). */
function linePageHref(line: CatalogueLine, frame: FrameSpec | null, extra: Record<string, string> = {}): string {
  const root = new URL('../', import.meta.url);
  const url = new URL(line === 'Classic' ? 'signature/' : './', root.href);
  if (frame && frame.line === line) url.searchParams.set('frame', frame.id);
  // The catalogue page's language (the try-on's own language is only set once it opens).
  url.searchParams.set('lang', detectLang());
  for (const [k, v] of Object.entries(extra)) url.searchParams.set(k, v);
  return url.href;
}

/**
 * A110Y: one engine per page. Each try-on owns a WebGL context, a worker with its own GPU
 * context and ~150 MB of wasm, and up to 150 MB of drawing buffers; a second one opened over a
 * first that was never closed (a badge clicked twice, a host page without a guard) doubled all
 * of it. The previous engine is closed first, and the page's unload closes the current one.
 */
let currentApp: TryOnApp | null = null;
window.addEventListener('pagehide', () => currentApp?.close());

function open(overrides: Partial<TryOnConfig> = {}, measurementStarted = false): TryOnApp {
  if (currentApp) {
    const previous = currentApp;
    currentApp = null;
    previous.close();
  }
  const config = resolveConfig(overrides);
  const container = document.createElement('div');
  document.body.append(container);
  warmSelectedFrame(config.frame);
  if (!measurementStarted) phonePanel?.begin('Ouverture', config.frame.id);
  const standalone = Boolean(catalogueRoot);
  /** QA pages (qa-flat.html) keep the old launch buttons and the model picker, without the catalogue. */
  const qaPage = !standalone && Boolean(legacyLauncher);
  let current = config.frame.id;
  const originalClose = config.onClose;
  config.onClose = () => {
    if (currentApp === app) currentApp = null;
    phonePanel?.closed(app);
    container.remove();
    if (standalone) leaveTryOn(current);
    originalClose?.();
  };
  const app = new TryOnApp(container, config);
  currentApp = app;
  phonePanel?.attach(app);
  if (standalone) enterTryOn();
  if (standalone || qaPage || config.framePicker === true) {
    // A42E « Modèles » view (mobile panel / desktop rail), A42F split CTA.
    let request = 0;
    const lang = getLang();
    const switchStatus = document.createElement('p');
    switchStatus.className = 'mt-model-switch-status'; switchStatus.setAttribute('role', 'status');
    switchStatus.hidden = true;
    container.querySelector('.mt-stage')?.append(switchStatus);
    const pickerFrames = config.catalogueLine ? LISTS[config.catalogueLine] : FRAME_CATALOGUE;
    const shopRoot = (window as Window & { Shopify?: { routes?: { root?: string } } }).Shopify?.routes?.root ?? '/';
    const view: FramesView = new FramesView(pickerFrames, config.frame.id, {
      line: config.catalogueLine ? (config.catalogueLine === 'Classic' ? 'Signature' : 'Handmade') : 'Maison Mata',
      pageHandle: standalone || qaPage ? undefined : config.frame.productHandle,
      productRoot: standalone || qaPage ? 'https://maisonmata.com/' : shopRoot,
      onCurrentPage: () => app.close(),
      // Standalone: the catalogue is right under the try-on (A200: or the other collection's page).
      // Embedded: the catalogue page of the collection shown, same tab.
      catalogueHref: standalone
        ? (frame, line) => (line && line !== pageLine ? linePageHref(line, frame) : catalogueSearch(frame.line === pageLine ? frame.id : null, getLang()))
        : (frame, line) => catalogueHref(config, frame, line),
      onSeeAll: standalone ? (frame, line) => {
        if (line && line !== pageLine) { app.close(); location.assign(linePageHref(line, frame)); return; }
        app.close();
      } : undefined,
      // A200: Handmade · Signature switch (each collection keeps its own list and page).
      ...(config.catalogueLine ? { collections: { current: config.catalogueLine, lists: LISTS } } : {}),
      onSelect: async frame => {
        const token = ++request;
        phonePanel?.begin('Changement', frame.id);
        switchStatus.hidden = false;
        switchStatus.textContent = translator(lang)("Preparing frame\u2026");
        try {
          const changed = await app.switchFrame(frame, () => loadCatalogueFrame(frame.id));
          if (token !== request) return;
          if (!changed) { switchStatus.textContent = translator(lang)("Wait for the try-on to start."); phonePanel?.cancelPending(); phonePanel?.attach(app); return; }
          view.setSelected(frame.id);
          current = frame.id;
          if (config.catalogueLine) config.catalogueLine = frame.line;
          if (standalone) rememberFrame(frame.id);
          phonePanel?.attach(app);
          switchStatus.hidden = true;
        } catch {
          if (token !== request) return;
          phonePanel?.cancelPending(); phonePanel?.attach(app);
          switchStatus.textContent = translator(lang)("Could not load. Try another frame.");
        }
      },
    });
    app.setFramesView(view);
  }
  void app.open();
  return app;
}

host.MataTryOn = { open, catalogue: FRAME_CATALOGUE, loadFrame: loadCatalogueFrame };

/* ---------------------------------------------------------------------------
   Standalone page: the Handmade catalogue.
   --------------------------------------------------------------------------- */

/** Keep `?frame=` in the address bar in step with the selection (no reload, no history entry). */
function rememberFrame(id: string | null): void {
  if (id) params.set('frame', id); else params.delete('frame');
  params.delete('autostart'); // a reload shows the catalogue, it does not reopen the camera
  const query = params.toString();
  history.replaceState(null, '', `${location.pathname}${query ? `?${query}` : ''}${location.hash}`);
}

/** The try-on covers the page: the catalogue stops scrolling and leaves the accessibility tree. */
function enterTryOn(): void {
  document.documentElement.classList.add('mt-cat--locked');
  if (catalogueRoot) catalogueRoot.inert = true;
}

/** Back on the catalogue: the frame last tried is selected, in view and focused. */
function leaveTryOn(frameId: string): void {
  document.documentElement.classList.remove('mt-cat--locked');
  if (catalogueRoot) catalogueRoot.inert = false;
  if (!catalogue) return;
  const known = LISTS[pageLine].some(f => f.id === frameId);
  catalogue.select(known ? frameId : catalogue.selectedId, { scroll: known, focus: true });
  rememberFrame(catalogue.selectedId);
}

function openFromCatalogue(frame: FrameSpec, debug = false): TryOnApp {
  rememberFrame(frame.id);
  return open({ frame: { id: frame.id } as FrameSpec, catalogueLine: frame.line, debug: debug || undefined });
}

if (catalogueRoot) {
  // Keep the selected storefront language, including when it differs from the browser.
  const lang = detectLang();
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  document.documentElement.classList.add('mt-cat-page');
  const requested = params.get('frame');
  const pageFrames = LISTS[pageLine];
  const preselected = pageFrames.some(f => f.id === requested) ? requested : null;
  const shapeParam = params.get('shape');
  catalogue = new CataloguePage(catalogueRoot, {
    frames: pageFrames,
    line: pageLine,
    // A200: the switch opens the other collection's page; the shape filter follows when it exists there.
    lines: { Handmade: HANDMADE.length, Classic: SIGNATURE.length },
    lineHref: (line, shape) => linePageHref(line, null, shape && shape !== 'all' ? { shape } : {}),
    shape: isShape(shapeParam) ? shapeParam : null,
    onShape: shape => {
      if (shape && shape !== 'all') params.set('shape', shape); else params.delete('shape');
      rememberFrame(catalogue?.selectedId ?? null);
    },
    selectedId: preselected,
    lang,
    onSelect: frame => {
      rememberFrame(frame?.id ?? null);
      if (frame) void loadCatalogueFrame(frame.id).then(warmSelectedFrame).catch(() => undefined);
    },
    onTry: frame => openFromCatalogue(frame),
    onDebug: frame => openFromCatalogue(frame, true),
  });
  // Defer warm-up until the page has painted. Other frame textures stay on demand.
  window.setTimeout(() => {
    try {
      const config = resolveConfig(preselected ? { catalogueLine: pageLine } : {});
      void warmEngine(config);
      if (preselected) void loadCatalogueFrame(preselected).then(warmSelectedFrame).catch(() => undefined);
    } catch { /* unknown ?frame=: nothing to warm */ }
  }, 250);
  // ?autostart=1 (E2E, « Try it on » opened in a new tab): open the try-on at once.
  if (params.get('autostart') === '1') {
    const legacy = requested && !preselected ? FRAME_CATALOGUE.find(f => f.id === requested) : undefined;
    if (preselected || legacy) openFromCatalogue((legacy ?? pageFrames.find(f => f.id === preselected))!, params.get('debug') === '1');
    else open(); // no frame: the historical default (all frames, first entry), used by the E2E suite
  }
}

// QA pages (qa-flat.html, used by e2e/cdn.spec.ts): the historical launch buttons.
if (!catalogueRoot && legacyLauncher) {
  legacyLauncher.addEventListener('click', () => open());
  document.getElementById('mt-launch-debug')?.addEventListener('click', () => open({ debug: true }));
  if (params.get('autostart') === '1') open();
}
